import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Command } from "cmdk";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type EntityOption = {
  id: string;
  label: string;
  /** Extra searchable text (codes, aliases, GSTIN, etc.) */
  keywords?: string;
  meta?: string;
};

const RECENT_LIMIT = 8;
const DENSE_PANEL_MIN = 224;

function recentStorageKey(key: string) {
  return `fs-entity-recent:${key}`;
}

function readRecent(key: string): string[] {
  try {
    const raw = localStorage.getItem(recentStorageKey(key));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function pushRecent(key: string, id: string) {
  const next = [id, ...readRecent(key).filter((x) => x !== id)].slice(0, RECENT_LIMIT);
  try {
    localStorage.setItem(recentStorageKey(key), JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
}

type EntityPickerProps = {
  value: string;
  onChange: (id: string) => void;
  options: EntityOption[];
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  /** Persists recent picks under this key (e.g. "vendors", "materials") */
  recentKey?: string;
  allowClear?: boolean;
  /** Compact control for spreadsheet cells */
  dense?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
};

type PanelPos = { top: number; left: number; width: number; maxHeight: number };

/**
 * Keyboard-first entity selector: type to filter, ↑↓, Enter.
 * Panel is portaled so it is not clipped inside overflow scroll grids.
 */
export function EntityPicker({
  value,
  onChange,
  options,
  placeholder = "Search…",
  emptyLabel = "No matches",
  disabled,
  recentKey,
  allowClear,
  dense,
  className,
  id,
  "aria-label": ariaLabel,
}: EntityPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [pos, setPos] = useState<PanelPos | null>(null);

  const selected = useMemo(
    () => options.find((o) => o.id === value) ?? null,
    [options, value],
  );

  const optionById = useMemo(() => {
    const map = new Map<string, EntityOption>();
    for (const o of options) map.set(o.id, o);
    return map;
  }, [options]);

  const recentOptions = useMemo(() => {
    if (!recentKey) return [];
    return recentIds.map((id) => optionById.get(id)).filter(Boolean) as EntityOption[];
  }, [recentIds, optionById, recentKey]);

  const updatePosition = useCallback(() => {
    const el = buttonRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.max(r.width, dense ? DENSE_PANEL_MIN : r.width);
    const spaceBelow = window.innerHeight - r.bottom - 8;
    const spaceAbove = r.top - 8;
    const preferBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(280, Math.max(120, preferBelow ? spaceBelow : spaceAbove));
    let left = r.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    const top = preferBelow ? r.bottom + 4 : Math.max(8, r.top - maxHeight - 4);
    setPos({ top, left, width, maxHeight });
  }, [dense]);

  useEffect(() => {
    if (!open || !recentKey) return;
    setRecentIds(readRecent(recentKey));
  }, [open, recentKey]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener("resize", onReposition);
    // capture scroll from any overflow ancestor (line grid, main, etc.)
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = useCallback(
    (id: string) => {
      onChange(id);
      if (recentKey && id) pushRecent(recentKey, id);
      setOpen(false);
      setQuery("");
    },
    [onChange, recentKey],
  );

  const clear = useCallback(() => {
    onChange("");
    setOpen(false);
    setQuery("");
  }, [onChange]);

  function openPanel() {
    if (disabled) return;
    setOpen(true);
  }

  const panel =
    open && pos
      ? createPortal(
          <div
            ref={panelRef}
            id={listId}
            role="listbox"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
              zIndex: 60,
            }}
            className="overflow-hidden rounded-md border border-border bg-popover shadow-lg"
          >
            <Command
              loop
              shouldFilter
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em] [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              <div className="border-b border-border px-1.5 py-1">
                <Command.Input
                  ref={inputRef}
                  value={query}
                  onValueChange={setQuery}
                  placeholder={placeholder}
                  className="fs-control-cell w-full border-0 bg-transparent outline-none placeholder:text-muted-foreground/70"
                />
              </div>
              <Command.List
                className="overflow-y-auto p-1"
                style={{ maxHeight: Math.max(80, pos.maxHeight - 40) }}
              >
                <Command.Empty className="py-4 text-center text-2xs text-muted-foreground">
                  {emptyLabel}
                </Command.Empty>

                {recentOptions.length > 0 && !query.trim() ? (
                  <Command.Group heading="Recent">
                    {recentOptions.map((opt) => (
                      <PickerItem
                        key={`r-${opt.id}`}
                        option={opt}
                        selected={opt.id === value}
                        onSelect={() => pick(opt.id)}
                      />
                    ))}
                  </Command.Group>
                ) : null}

                <Command.Group heading={recentOptions.length && !query.trim() ? "All" : undefined}>
                  {options.map((opt) => (
                    <PickerItem
                      key={opt.id}
                      option={opt}
                      selected={opt.id === value}
                      onSelect={() => pick(opt.id)}
                    />
                  ))}
                </Command.Group>
              </Command.List>
            </Command>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn("relative min-w-0", className)}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel ?? placeholder}
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        onClick={() => (open ? (setOpen(false), setQuery("")) : openPanel())}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && (e.altKey || !open)) {
            e.preventDefault();
            openPanel();
          }
        }}
        className={cn(
          "flex w-full items-center gap-1 text-left transition-[border-color,box-shadow]",
          dense
            ? "fs-control-cell border border-border bg-card hover:border-border-strong focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring/30"
            : "fs-control rounded-md border border-input bg-card px-[var(--fs-control-px)] focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25",
          disabled && "cursor-not-allowed opacity-60",
          !selected && "text-muted-foreground",
        )}
      >
        <span className="min-w-0 flex-1 truncate text-[var(--fs-control-text)]">
          {selected ? selected.label : placeholder}
        </span>
        {allowClear && value ? (
          <span
            role="button"
            tabIndex={-1}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }
            }}
          >
            <X className="size-3" />
          </span>
        ) : (
          <ChevronsUpDown className="size-3 shrink-0 text-muted-foreground" />
        )}
      </button>
      {panel}
    </div>
  );
}

function PickerItem({
  option,
  selected,
  onSelect,
}: {
  option: EntityOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const searchValue = `${option.label} ${option.keywords ?? ""} ${option.meta ?? ""}`;
  return (
    <Command.Item
      value={searchValue}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-xs aria-selected:bg-accent aria-selected:text-accent-foreground"
    >
      <Check className={cn("size-3 shrink-0", selected ? "opacity-100" : "opacity-0")} />
      <span className="min-w-0 flex-1 truncate text-foreground">{option.label}</span>
      {option.meta ? (
        <span className="shrink-0 truncate text-[10px] text-muted-foreground">{option.meta}</span>
      ) : null}
    </Command.Item>
  );
}
